'use client';

import { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { createClient } from '@/lib/supabase/client';

export type UserRole = 'member' | 'coach' | 'admin' | 'super_admin';
export type UserLevel = 'beginner' | 'intermediate' | 'advanced';

export interface UserProfile {
  id: string;
  email: string;
  display_name: string;
  nickname?: string;
  avatar?: string;
  instagram?: string;
  wechat?: string;
  phone?: string;
  preferred_language: 'EN' | 'ZH';
  role: UserRole;
  level: UserLevel;
  created_at: string;
}

interface AuthContextType {
  user: any;
  session: any;
  userProfile: UserProfile | null;
  loading: boolean;
  /** True while the user_profiles row is still being fetched. Never treat null profile as "no role" while this is true. */
  profileLoading: boolean;
  role: UserRole | null;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  isCoach: boolean;
  isMember: boolean;
  /** True when all retries have failed and we cannot verify permissions */
  profileFetchFailed: boolean;
  /** Single source of truth for role checks. Returns null while auth or profile is loading. */
  getCurrentUserRole: () => UserRole | null;
  signUp: (email: string, password: string, metadata?: Record<string, any>) => Promise<any>;
  signIn: (email: string, password: string) => Promise<any>;
  signInWithGoogle: () => Promise<any>;
  signInWithApple: () => Promise<any>;
  signOut: () => Promise<void>;
  getCurrentUser: () => Promise<any>;
  isEmailVerified: () => boolean;
  getUserProfile: () => Promise<UserProfile | null>;
  updateProfile: (updates: Partial<UserProfile>) => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({} as AuthContextType);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

// Admin roles — used for in-memory cache check
const ADMIN_ROLES: UserRole[] = ['admin', 'super_admin', 'coach'];

// ─── Profile fetch with per-attempt timeout ───────────────────────────────────
// Each attempt has a 15-second timeout. Returns null on timeout (treated as unknown).
async function fetchProfileOnce(
  supabase: ReturnType<typeof createClient>,
  userId: string,
  timeoutMs: number = 15000
): Promise<{ profile: UserProfile | null; timedOut: boolean }> {
  return new Promise((resolve) => {
    let settled = false;
    const timer = setTimeout(() => {
      if (!settled) {
        settled = true;
        console.warn('[Auth] fetchUserProfile timed out for user:', userId);
        resolve({ profile: null, timedOut: true });
      }
    }, timeoutMs);

    supabase
      .from('user_profiles')
      .select('*')
      .eq('id', userId)
      .maybeSingle()
      .then(({ data, error }) => {
        if (!settled) {
          settled = true;
          clearTimeout(timer);
          if (error) {
            console.warn('[Auth] profile query failed:', error.message);
            resolve({ profile: null, timedOut: false });
          } else {
            console.log('[Auth] profile loaded — user_id:', userId, '| role:', (data as any)?.role ?? 'null');
            resolve({ profile: data as UserProfile | null, timedOut: false });
          }
        }
      })
      .catch((err) => {
        if (!settled) {
          settled = true;
          clearTimeout(timer);
          console.warn('[Auth] profile query exception:', err?.message);
          resolve({ profile: null, timedOut: false });
        }
      });
  });
}

// ─── Profile fetch with retry (3 attempts: 1s / 2s / 4s delay between) ───────
// Returns { profile, exhausted } where exhausted=true means all retries failed.
async function fetchProfileWithRetry(
  supabase: ReturnType<typeof createClient>,
  userId: string,
  onAttempt?: (attempt: number) => void
): Promise<{ profile: UserProfile | null; exhausted: boolean }> {
  const delays = [0, 1000, 2000, 4000]; // attempt 1 immediate, then 1s, 2s, 4s
  const maxAttempts = 3;

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    if (attempt > 1) {
      const delay = delays[attempt - 1];
      console.log(`[Auth] retrying profile fetch (attempt ${attempt}/${maxAttempts}) in ${delay}ms...`);
      await new Promise((r) => setTimeout(r, delay));
    }

    if (onAttempt) onAttempt(attempt);

    const { profile, timedOut } = await fetchProfileOnce(supabase, userId, 15000);

    if (profile !== null) {
      // Got a real result from Supabase — return it regardless of role
      console.log('[Auth] profile fetch succeeded on attempt', attempt, '| role:', profile.role);
      return { profile, exhausted: false };
    }

    if (!timedOut) {
      // Supabase responded but returned no row (user_profiles row doesn't exist yet)
      // No point retrying — the row simply isn't there
      console.warn('[Auth] profile row not found for user:', userId, '— stopping retries');
      return { profile: null, exhausted: false };
    }

    console.warn(`[Auth] profile fetch attempt ${attempt} timed out`);
  }

  console.warn('[Auth] all profile fetch attempts exhausted for user:', userId);
  return { profile: null, exhausted: true };
}

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [session, setSession] = useState<any>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileFetchFailed, setProfileFetchFailed] = useState(false);

  // ── In-memory admin role cache (session-scoped, never persisted) ──────────
  // If a user was confirmed as admin/super_admin/coach in this browser session,
  // we keep that knowledge here so a temporary timeout never flashes "Access Restricted".
  const confirmedAdminRoleRef = useRef<UserRole | null>(null);

  const supabase = createClient();
  const fetchingProfileRef = useRef(false);
  const authTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchUserProfile = useCallback(async (userId: string): Promise<UserProfile | null> => {
    setProfileFetchFailed(false);
    const { profile, exhausted } = await fetchProfileWithRetry(supabase, userId);

    if (profile !== null) {
      // Confirmed result from Supabase — update cache if admin role
      if (profile.role && ADMIN_ROLES.includes(profile.role)) {
        confirmedAdminRoleRef.current = profile.role;
        console.log('[Auth] admin role cached in memory:', profile.role);
      } else {
        // Confirmed non-admin — clear cache
        confirmedAdminRoleRef.current = null;
      }
    } else if (exhausted) {
      // All retries failed — mark as failed but do NOT clear admin cache
      setProfileFetchFailed(true);
      console.warn('[Auth] profile fetch exhausted — keeping last known admin cache:', confirmedAdminRoleRef.current);
    }
    // If profile is null but not exhausted (shouldn't happen with current logic), treat as unknown

    return profile;
  }, []); // stable

  useEffect(() => {
    let cancelled = false;

    // Hard safety net: if auth takes > 20s total, force loading=false
    authTimeoutRef.current = setTimeout(() => {
      if (!cancelled) {
        console.warn('[Auth] auth hydration timed out after 20s — forcing loading=false');
        setProfileLoading(false);
        setLoading(false);
      }
    }, 20000);

    const clearAuthTimeout = () => {
      if (authTimeoutRef.current) {
        clearTimeout(authTimeoutRef.current);
        authTimeoutRef.current = null;
      }
    };

    // Get initial session once
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (cancelled) return;
      setSession(session);
      setUser(session?.user ?? null);

      if (session?.user) {
        console.log('[Auth] initial session found, user_id:', session.user.id, '| email:', session.user.email);
        if (!fetchingProfileRef.current) {
          fetchingProfileRef.current = true;
          setProfileLoading(true);
          const profile = await fetchUserProfile(session.user.id);
          if (!cancelled) {
            setUserProfile(profile);
            setProfileLoading(false);
            fetchingProfileRef.current = false;
            console.log('[Auth] role resolved — user_id:', session.user.id, '| email:', session.user.email, '| role:', profile?.role ?? 'null (no profile row)', '| isAdmin:', profile?.role === 'admin' || profile?.role === 'super_admin', '| cachedAdminRole:', confirmedAdminRoleRef.current);
          }
        }
      } else {
        console.log('[Auth] no initial session — unauthenticated');
      }

      if (!cancelled) {
        clearAuthTimeout();
        setLoading(false);
        console.log('[Auth] auth status resolved:', session?.user ? 'authenticated' : 'unauthenticated');
      }
    }).catch((err) => {
      if (!cancelled) {
        console.warn('[Auth] getSession failed:', err?.message);
        clearAuthTimeout();
        setProfileLoading(false);
        setLoading(false);
      }
    });

    // Listen for auth state changes (sign in / sign out)
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (_event, session) => {
      if (cancelled) return;
      setSession(session);
      setUser(session?.user ?? null);

      if (session?.user) {
        if (!fetchingProfileRef.current) {
          fetchingProfileRef.current = true;
          setProfileLoading(true);
          const profile = await fetchUserProfile(session.user.id);
          if (!cancelled) {
            setUserProfile(profile);
            setProfileLoading(false);
            fetchingProfileRef.current = false;
            console.log('[Auth] onAuthStateChange role resolved — user_id:', session.user.id, '| email:', session.user.email, '| role:', profile?.role ?? 'null', '| isAdmin:', profile?.role === 'admin' || profile?.role === 'super_admin', '| cachedAdminRole:', confirmedAdminRoleRef.current);
          }
        }
      } else {
        setUserProfile(null);
        setProfileLoading(false);
        setProfileFetchFailed(false);
        confirmedAdminRoleRef.current = null; // clear cache on sign out
        fetchingProfileRef.current = false;
      }

      if (!cancelled) {
        clearAuthTimeout();
        setLoading(false);
      }
    });

    return () => {
      cancelled = true;
      clearAuthTimeout();
      subscription.unsubscribe();
    };
  }, []); // run once on mount only

  // ── Single source of truth for role ──────────────────────────────────────
  // Returns null while auth OR profile is still loading.
  const getCurrentUserRole = useCallback((): UserRole | null => {
    if (loading || profileLoading) return null;
    // If fetch failed but we have a cached admin role, use it
    if (profileFetchFailed && confirmedAdminRoleRef.current) {
      return confirmedAdminRoleRef.current;
    }
    return (userProfile?.role as UserRole) ?? null;
  }, [loading, profileLoading, profileFetchFailed, userProfile]);

  // ── Derived role helpers ──────────────────────────────────────────────────
  // CRITICAL: never evaluate role while loading OR profileLoading is true.
  // If profile fetch failed but we have a cached admin role, use the cache.
  const role: UserRole | null = (() => {
    if (loading || profileLoading) return null;
    if (profileFetchFailed && confirmedAdminRoleRef.current) {
      // Timeout/retry exhausted but user was previously confirmed admin — keep that
      return confirmedAdminRoleRef.current;
    }
    return (userProfile?.role as UserRole) ?? null;
  })();

  const isAdmin = role === 'admin' || role === 'super_admin';
  const isSuperAdmin = role === 'super_admin';
  const isCoach = role === 'coach' || isAdmin;
  const isMember = !!user;

  // Email/Password Sign Up
  const signUp = async (email: string, password: string, metadata: Record<string, any> = {}) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          display_name: metadata.displayName || metadata.full_name || '',
          nickname: metadata.nickname || '',
          avatar_url: metadata.avatarUrl || '',
          preferred_language: metadata.preferredLanguage || 'EN',
          role: 'member',
        },
        emailRedirectTo: `${typeof window !== 'undefined' ? window.location.origin : ''}/auth/callback`,
      },
    });
    if (error) throw error;
    return data;
  };

  // Email/Password Sign In
  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    return data;
  };

  // Google OAuth
  const signInWithGoogle = async () => {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${typeof window !== 'undefined' ? window.location.origin : ''}/auth/callback`,
      },
    });
    if (error) throw error;
    return data;
  };

  // Apple OAuth
  const signInWithApple = async () => {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'apple',
      options: {
        redirectTo: `${typeof window !== 'undefined' ? window.location.origin : ''}/auth/callback`,
      },
    });
    if (error) throw error;
    return data;
  };

  // Sign Out
  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    setUserProfile(null);
    confirmedAdminRoleRef.current = null;
  };

  // Get Current User
  const getCurrentUser = async () => {
    const { data: { user }, error } = await supabase.auth.getUser();
    if (error) throw error;
    return user;
  };

  // Check if Email is Verified
  const isEmailVerified = () => {
    return user?.email_confirmed_at != null;
  };

  // Get User Profile from Database
  const getUserProfile = async (): Promise<UserProfile | null> => {
    if (!user) return null;
    return fetchUserProfile(user.id);
  };

  // Update profile
  const updateProfile = async (updates: Partial<UserProfile>) => {
    if (!user) throw new Error('Not authenticated');
    const { error } = await supabase
      .from('user_profiles')
      .update(updates)
      .eq('id', user.id);
    if (error) throw error;
    const updated = await fetchUserProfile(user.id);
    setUserProfile(updated);
  };

  const value: AuthContextType = {
    user,
    session,
    userProfile,
    loading,
    profileLoading,
    profileFetchFailed,
    role,
    isAdmin,
    isSuperAdmin,
    isCoach,
    isMember,
    getCurrentUserRole,
    signUp,
    signIn,
    signInWithGoogle,
    signInWithApple,
    signOut,
    getCurrentUser,
    isEmailVerified,
    getUserProfile,
    updateProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
